import React, { useState, useEffect } from 'react';
import * as proxmoxHostService from '../../services/proxmoxHostService';
import type { ProxmoxHost, CreateProxmoxHostData, UpdateProxmoxHostData, ProxmoxHostStats, CapacityOverview } from '../../services/proxmoxHostService';

const ProxmoxHostManagement: React.FC = () => {
  const [hosts, setHosts] = useState<ProxmoxHost[]>([]);
  const [stats, setStats] = useState<ProxmoxHostStats | null>(null);
  const [capacityOverview, setCapacityOverview] = useState<CapacityOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingHost, setEditingHost] = useState<ProxmoxHost | null>(null);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [selectedHostStats, setSelectedHostStats] = useState<any>(null);
  const [testingConnection, setTestingConnection] = useState<number | null>(null);
  const [testingCredentials, setTestingCredentials] = useState(false);
  const [syncingResources, setSyncingResources] = useState(false);

  const [formData, setFormData] = useState<CreateProxmoxHostData>({
    name: '',
    hostname: '',
    port: 8006,
    username: 'root@pam',
    password: '',
    ipv6_block: '',
    ipv6_gateway: '',
    datacenter_location: '',
    max_vms: 100,
    is_default: false,
    notes: ''
  });

  useEffect(() => {
    fetchHosts();
    fetchStats();
    fetchCapacityOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const fetchHosts = async () => {
    try {
      setLoading(true);
      const data = await proxmoxHostService.getAllProxmoxHosts();
      setHosts(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch Proxmox hosts');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await proxmoxHostService.getOverviewStats();
      setStats(data);
    } catch (err: any) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const fetchCapacityOverview = async () => {
    try {
      const data = await proxmoxHostService.getCapacityOverview();
      setCapacityOverview(data);
    } catch (err: any) {
      console.error('Failed to fetch capacity overview:', err);
    }
  };

  const handleSyncAllResources = async () => {
    try {
      setSyncingResources(true);
      const result = await proxmoxHostService.syncAllHostResources();
      setSuccess(`Synchronized ${result.hostsUpdated} of ${result.totalHosts} hosts`);
      fetchHosts();
      fetchCapacityOverview();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to sync resources');
    } finally {
      setSyncingResources(false);
    }
  };

  const handleTestCredentials = async () => {
    if (!formData.hostname || !formData.username || !formData.password) {
      setError('Please fill in hostname, username, and password first');
      return;
    }

    try {
      setTestingCredentials(true);
      const result = await proxmoxHostService.testProxmoxCredentials({
        hostname: formData.hostname,
        port: formData.port,
        username: formData.username,
        password: formData.password
      });

      if (result.success) {
        setSuccess('Connection test successful!');
      } else {
        setError(result.message || 'Connection test failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to test credentials');
    } finally {
      setTestingCredentials(false);
    }
  };

  const handleTestConnection = async (id: number) => {
    try {
      setTestingConnection(id);
      const result = await proxmoxHostService.testProxmoxConnection(id);

      if (result.success) {
        setSuccess(`Connection test successful for host ID ${id}`);
      } else {
        setError(result.message || 'Connection test failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to test connection');
    } finally {
      setTestingConnection(null);
    }
  };

  const handleViewStats = async (host: ProxmoxHost) => {
    try {
      const statsData = await proxmoxHostService.getProxmoxHostStats(host.id);
      setSelectedHostStats({ host, stats: statsData });
      setShowStatsModal(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch host statistics');
    }
  };

  const handleSetDefault = async (id: number) => {
    if (!window.confirm('Are you sure you want to set this as the default host?')) {
      return;
    }

    try {
      await proxmoxHostService.setDefaultHost(id);
      setSuccess('Default host updated successfully');
      fetchHosts();
      fetchStats();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to set default host');
    }
  };

  const handleStatusChange = async (id: number, status: 'active' | 'maintenance' | 'offline') => {
    try {
      await proxmoxHostService.updateHostStatus(id, status);
      setSuccess(`Host status updated to ${status}`);
      fetchHosts();
      fetchStats();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update host status');
    }
  };

  const handleSyncVMCount = async (id: number) => {
    try {
      const result = await proxmoxHostService.syncVMCount(id);
      setSuccess(`VM count synced: ${result.current_vms} VMs`);
      fetchHosts();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to sync VM count');
    }
  };

  const handleSubmitAdd = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await proxmoxHostService.createProxmoxHost(formData);
      setSuccess('Proxmox host created successfully');
      setShowAddModal(false);
      resetForm();
      fetchHosts();
      fetchStats();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create Proxmox host');
    }
  };

  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingHost) return;

    try {
      const updateData: UpdateProxmoxHostData = { ...formData };
      // Don't send password if it's empty (not being updated)
      if (!formData.password) {
        delete (updateData as any).password;
      }

      await proxmoxHostService.updateProxmoxHost(editingHost.id, updateData);
      setSuccess('Proxmox host updated successfully');
      setShowEditModal(false);
      setEditingHost(null);
      resetForm();
      fetchHosts();
      fetchStats();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update Proxmox host');
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await proxmoxHostService.deleteProxmoxHost(id);
      setSuccess('Proxmox host deleted successfully');
      fetchHosts();
      fetchStats();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete Proxmox host');
    }
  };

  const openEditModal = (host: ProxmoxHost) => {
    setEditingHost(host);
    setFormData({
      name: host.name,
      hostname: host.hostname,
      port: host.port,
      username: host.username,
      password: '', // Don't populate password
      ipv6_block: host.ipv6_block,
      ipv6_gateway: host.ipv6_gateway,
      datacenter_location: host.datacenter_location,
      max_vms: host.max_vms,
      notes: host.notes || ''
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      hostname: '',
      port: 8006,
      username: 'root@pam',
      password: '',
      ipv6_block: '',
      ipv6_gateway: '',
      datacenter_location: '',
      max_vms: 100,
      is_default: false,
      notes: ''
    });
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      active: 'bg-green-100 text-green-800 border-green-300',
      maintenance: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      offline: 'bg-red-100 text-red-800 border-red-300'
    };

    const icons = {
      active: '🟢',
      maintenance: '🟡',
      offline: '🔴'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${badges[status as keyof typeof badges] || badges.offline}`}>
        {icons[status as keyof typeof icons]} {status.toUpperCase()}
      </span>
    );
  };

  const FormModal = ({ isEdit = false }: { isEdit?: boolean }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={(e) => e.target === e.currentTarget && (isEdit ? setShowEditModal(false) : setShowAddModal(false))}>
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-lg">
          <h2 className="text-2xl font-bold">
            {isEdit ? '📝 Edit Proxmox Host' : '➕ Add New Proxmox Host'}
          </h2>
        </div>

        <form onSubmit={isEdit ? handleSubmitEdit : handleSubmitAdd} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Host Name *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., OVH Canada Primary"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hostname/IP *</label>
              <input
                type="text"
                required
                value={formData.hostname}
                onChange={(e) => setFormData(prev => ({ ...prev, hostname: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 15.204.107.159"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Port *</label>
              <input
                type="number"
                required
                value={formData.port}
                onChange={(e) => setFormData(prev => ({ ...prev, port: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="8006"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
              <input
                type="text"
                required
                value={formData.username}
                onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="root@pam"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password {isEdit ? '(leave blank to keep unchanged)' : '*'}
              </label>
              <input
                type="password"
                required={!isEdit}
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="••••••••"
              />
              {!isEdit && (
                <button
                  type="button"
                  onClick={handleTestCredentials}
                  disabled={testingCredentials}
                  className="mt-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 text-sm"
                >
                  {testingCredentials ? 'Testing...' : '🔍 Test Credentials'}
                </button>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">IPv6 Block *</label>
              <input
                type="text"
                required
                value={formData.ipv6_block}
                onChange={(e) => setFormData(prev => ({ ...prev, ipv6_block: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="2604:2dc0:209:9f00::/56"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">IPv6 Gateway *</label>
              <input
                type="text"
                required
                value={formData.ipv6_gateway}
                onChange={(e) => setFormData(prev => ({ ...prev, ipv6_gateway: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="2604:2dc0:209:9f00::1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Datacenter Location *</label>
              <input
                type="text"
                required
                value={formData.datacenter_location}
                onChange={(e) => setFormData(prev => ({ ...prev, datacenter_location: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Canada - OVH"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max VMs *</label>
              <input
                type="number"
                required
                value={formData.max_vms}
                onChange={(e) => setFormData(prev => ({ ...prev, max_vms: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="100"
              />
            </div>

            {!isEdit && (
              <div className="md:col-span-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_default: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Set as default host for new VMs</span>
                </label>
              </div>
            )}

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Additional notes about this host..."
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => {
                isEdit ? setShowEditModal(false) : setShowAddModal(false);
                resetForm();
                setEditingHost(null);
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {isEdit ? 'Update Host' : 'Add Host'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
          <p className="text-red-700 font-medium">⚠️ {error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
          <p className="text-green-700 font-medium">✅ {success}</p>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center text-white">
          <div>
            <h2 className="text-3xl font-bold mb-1">🖥️ Proxmox Host Management</h2>
            <p className="text-blue-100">Manage multiple Proxmox servers for VPS provisioning and load balancing</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-6 py-3 bg-white text-blue-600 rounded-lg hover:bg-blue-50 font-bold shadow-xl text-lg flex items-center gap-2 transition-transform hover:scale-105"
          >
            <span className="text-2xl">➕</span>
            <span>Add Host</span>
          </button>
        </div>
      </div>

      {/* Enterprise Capacity Overview */}
      {capacityOverview && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">📊 Enterprise Capacity Overview</h3>
            <button
              onClick={handleSyncAllResources}
              disabled={syncingResources}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 text-sm flex items-center gap-2"
            >
              {syncingResources ? '🔄 Syncing...' : '🔄 Sync All Resources'}
            </button>
          </div>

          {/* Capacity Bars */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* RAM Usage */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">🧠 RAM</span>
                <span className={`text-sm font-bold ${
                  parseFloat(String(capacityOverview.utilizationPercent.ram || 0)) >= 80 ? 'text-red-600' :
                  parseFloat(String(capacityOverview.utilizationPercent.ram || 0)) >= 70 ? 'text-yellow-600' :
                  'text-green-600'
                }`}>
                  {parseFloat(String(capacityOverview.utilizationPercent.ram || 0)).toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all ${
                    parseFloat(String(capacityOverview.utilizationPercent.ram || 0)) >= 80 ? 'bg-red-500' :
                    parseFloat(String(capacityOverview.utilizationPercent.ram || 0)) >= 70 ? 'bg-yellow-500' :
                    'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(parseFloat(String(capacityOverview.utilizationPercent.ram || 0)), 100)}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500">
                {parseFloat(String(capacityOverview.allocatedResources.ram_gb || 0)).toFixed(1)} / {capacityOverview.totalCapacity.ram_gb} GB
              </p>
            </div>

            {/* Storage Usage */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">💾 Storage</span>
                <span className={`text-sm font-bold ${
                  parseFloat(String(capacityOverview.utilizationPercent.storage || 0)) >= 80 ? 'text-red-600' :
                  parseFloat(String(capacityOverview.utilizationPercent.storage || 0)) >= 70 ? 'text-yellow-600' :
                  'text-green-600'
                }`}>
                  {parseFloat(String(capacityOverview.utilizationPercent.storage || 0)).toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all ${
                    parseFloat(String(capacityOverview.utilizationPercent.storage || 0)) >= 80 ? 'bg-red-500' :
                    parseFloat(String(capacityOverview.utilizationPercent.storage || 0)) >= 70 ? 'bg-yellow-500' :
                    'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(parseFloat(String(capacityOverview.utilizationPercent.storage || 0)), 100)}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500">
                {capacityOverview.allocatedResources.storage_gb} / {capacityOverview.totalCapacity.storage_gb} GB
              </p>
            </div>

            {/* CPU Usage */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">⚡ CPU Cores</span>
                <span className={`text-sm font-bold ${
                  parseFloat(String(capacityOverview.utilizationPercent.cpu || 0)) >= 80 ? 'text-red-600' :
                  parseFloat(String(capacityOverview.utilizationPercent.cpu || 0)) >= 70 ? 'text-yellow-600' :
                  'text-green-600'
                }`}>
                  {parseFloat(String(capacityOverview.utilizationPercent.cpu || 0)).toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all ${
                    parseFloat(String(capacityOverview.utilizationPercent.cpu || 0)) >= 80 ? 'bg-red-500' :
                    parseFloat(String(capacityOverview.utilizationPercent.cpu || 0)) >= 70 ? 'bg-yellow-500' :
                    'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(parseFloat(String(capacityOverview.utilizationPercent.cpu || 0)), 100)}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500">
                {capacityOverview.allocatedResources.cpu_cores} / {capacityOverview.totalCapacity.cpu_cores} cores
              </p>
            </div>

            {/* VM Count */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">🖥️ VMs</span>
                <span className={`text-sm font-bold ${
                  parseFloat(String(capacityOverview.utilizationPercent.vms || 0)) >= 80 ? 'text-red-600' :
                  parseFloat(String(capacityOverview.utilizationPercent.vms || 0)) >= 70 ? 'text-yellow-600' :
                  'text-green-600'
                }`}>
                  {parseFloat(String(capacityOverview.utilizationPercent.vms || 0)).toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all ${
                    parseFloat(String(capacityOverview.utilizationPercent.vms || 0)) >= 80 ? 'bg-red-500' :
                    parseFloat(String(capacityOverview.utilizationPercent.vms || 0)) >= 70 ? 'bg-yellow-500' :
                    'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(parseFloat(String(capacityOverview.utilizationPercent.vms || 0)), 100)}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500">
                {capacityOverview.allocatedResources.current_vms} / {capacityOverview.totalCapacity.max_vms} VMs
              </p>
            </div>
          </div>

          {/* Hosts Needing Attention */}
          {capacityOverview.hostsNeedingAttention.length > 0 && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm font-medium text-yellow-800 mb-2">
                ⚠️ {capacityOverview.hostsNeedingAttention.length} host(s) above 70% capacity:
              </p>
              <div className="flex flex-wrap gap-2">
                {capacityOverview.hostsNeedingAttention.map(host => (
                  <span key={host.id} className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs">
                    {host.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg shadow-sm border border-blue-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Total Hosts</p>
                <p className="text-3xl font-bold text-blue-900">{stats.totalHosts}</p>
              </div>
              <div className="text-4xl opacity-50">🖥️</div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg shadow-sm border border-green-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Active Hosts</p>
                <p className="text-3xl font-bold text-green-900">{stats.activeHosts}</p>
              </div>
              <div className="text-4xl opacity-50">🟢</div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg shadow-sm border border-purple-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">Total VMs</p>
                <p className="text-3xl font-bold text-purple-900">{stats.totalVMs}/{stats.totalCapacity}</p>
              </div>
              <div className="text-4xl opacity-50">📊</div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg shadow-sm border border-orange-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600">Utilization</p>
                <p className="text-3xl font-bold text-orange-900">{stats.utilizationPercentage.toFixed(1)}%</p>
              </div>
              <div className="text-4xl opacity-50">📈</div>
            </div>
          </div>
        </div>
      )}

      {/* Hosts Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Proxmox Hosts</h3>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading hosts...</p>
          </div>
        ) : hosts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p className="text-xl mb-2">📭 No Proxmox hosts configured yet</p>
            <p className="text-sm">Click "Add Host" to add your first Proxmox server</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hostname</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resources</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">VMs</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Default</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {hosts.map((host) => {
                  const allocatedRam = parseFloat(String(host.allocated_ram_gb || 0));
                  const totalRam = parseFloat(String(host.ram_gb || 0));
                  const allocatedStorage = parseFloat(String(host.allocated_storage_gb || 0));
                  const totalStorage = parseFloat(String(host.storage_gb || 0));
                  const ramPercent = totalRam > 0 ? (allocatedRam / totalRam * 100) : 0;
                  const storagePercent = totalStorage > 0 ? (allocatedStorage / totalStorage * 100) : 0;
                  return (
                  <tr key={host.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{host.name}</div>
                      <div className="text-xs text-gray-500">{host.datacenter_location}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {host.hostname}:{host.port}
                    </td>
                    <td className="px-6 py-4">
                      {/* RAM */}
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs w-16">🧠 RAM:</span>
                        <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[100px]">
                          <div
                            className={`h-2 rounded-full ${ramPercent > 80 ? 'bg-red-500' : ramPercent > 60 ? 'bg-yellow-500' : 'bg-green-500'}`}
                            style={{ width: `${Math.min(ramPercent, 100)}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-500">{allocatedRam.toFixed(1)}/{totalRam}GB</span>
                      </div>
                      {/* Storage */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs w-16">💾 Disk:</span>
                        <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[100px]">
                          <div
                            className={`h-2 rounded-full ${storagePercent > 80 ? 'bg-red-500' : storagePercent > 60 ? 'bg-yellow-500' : 'bg-green-500'}`}
                            style={{ width: `${Math.min(storagePercent, 100)}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-500">{allocatedStorage}/{totalStorage}GB</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {host.current_vms} / {host.max_vms}
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                        <div
                          className={`h-2 rounded-full ${
                            (host.current_vms / host.max_vms) > 0.8 ? 'bg-red-500' :
                            (host.current_vms / host.max_vms) > 0.6 ? 'bg-yellow-500' :
                            'bg-green-500'
                          }`}
                          style={{ width: `${(host.current_vms / host.max_vms) * 100}%` }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(host.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {host.is_default ? (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                          ⭐ Default
                        </span>
                      ) : (
                        <button
                          onClick={() => handleSetDefault(host.id)}
                          className="text-blue-600 hover:text-blue-800 text-xs underline"
                        >
                          Set Default
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                      <button
                        onClick={() => handleTestConnection(host.id)}
                        disabled={testingConnection === host.id}
                        className="text-green-600 hover:text-green-800 font-medium disabled:text-gray-400"
                        title="Test Connection"
                      >
                        {testingConnection === host.id ? '🔄' : '🔍'}
                      </button>
                      <button
                        onClick={() => handleViewStats(host)}
                        className="text-purple-600 hover:text-purple-800 font-medium"
                        title="View Stats"
                      >
                        📊
                      </button>
                      <button
                        onClick={() => openEditModal(host)}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleSyncVMCount(host.id)}
                        className="text-orange-600 hover:text-orange-800 font-medium"
                        title="Sync VM Count"
                      >
                        🔄
                      </button>

                      {/* Status Dropdown */}
                      <select
                        value={host.status}
                        onChange={(e) => handleStatusChange(host.id, e.target.value as any)}
                        className="text-xs border border-gray-300 rounded px-1 py-0.5"
                        title="Change Status"
                      >
                        <option value="active">Active</option>
                        <option value="maintenance">Maintenance</option>
                        <option value="offline">Offline</option>
                      </select>

                      <button
                        onClick={() => handleDelete(host.id, host.name)}
                        className="text-red-600 hover:text-red-800 font-medium"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddModal && <FormModal />}
      {showEditModal && <FormModal isEdit />}

      {/* Stats Modal */}
      {showStatsModal && selectedHostStats && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 rounded-t-lg">
              <h2 className="text-2xl font-bold">📊 Host Statistics: {selectedHostStats.host.name}</h2>
            </div>

            <div className="p-6">
              <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                {JSON.stringify(selectedHostStats.stats, null, 2)}
              </pre>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t">
              <button
                onClick={() => {
                  setShowStatsModal(false);
                  setSelectedHostStats(null);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProxmoxHostManagement;
